import React from 'react';
import ReactDOM from 'react-dom';
import styles from './BlueprintModal.module.css';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';

/**
 * Interface for Blueprint data - flexible to support any JSON structure
 */
interface BlueprintData {
  blueprint_title?: string;
  version?: string;
  app_name?: string;
  app_id?: string;
  [key: string]: any;
}

interface BlueprintModalProps {
  data: BlueprintData;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Formats a JSON key into a readable label.
 * Examples: user_stories → "User Stories", visual_vibe → "Visual Vibe"
 */
const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Fields shown in header, not in body
 */
const HEADER_FIELDS = ['blueprint_title', 'version', 'app_name', 'app_id'];

/**
 * BlueprintModal Component
 * 
 * Full-screen modal rendering Blueprint JSON as a PRD-style document.
 * Clean, readable layout with generous spacing.
 */
const BlueprintModal: React.FC<BlueprintModalProps> = ({ data, isOpen, onClose }) => {
  // Handle ESC key to close modal
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (!isOpen) return;
    
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const title = data.blueprint_title || 'Blueprint';
  const version = data.version ? `v${data.version}` : '';

  /**
   * Renders JSON value based on type - PRD document style
   */
  const renderValue = (key: string, value: any, depth: number = 0): React.ReactNode => {
    if (HEADER_FIELDS.includes(key)) return null;

    const label = formatLabel(key);

    // String value - show as labeled paragraph at depth 0, plain text otherwise
    if (typeof value === 'string') {
      if (depth === 0) {
        return (
          <div key={key} className={styles.section}>
            <div className={styles.sectionTitle}>{label}</div>
            <p className={styles.paragraph}>{value}</p>
          </div>
        );
      }
      return (
        <div key={key} className={styles.field}>
          <span className={styles.fieldLabel}>{label}:</span>
          <span className={styles.fieldValue}>{value}</span>
        </div>
      );
    }

    // Array value - render as bullet list
    if (Array.isArray(value)) {
      return (
        <div key={key} className={styles.section}>
          <div className={styles.sectionTitle}>{label}</div>
          <ul className={styles.list}>
            {value.map((item, index) => (
              <li key={index} className={styles.listItem}>
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    // Object value - nested section
    if (typeof value === 'object' && value !== null) {
      return (
        <div key={key} className={styles.section}>
          <div className={depth === 0 ? styles.sectionTitle : styles.subSectionTitle}>
            {label}
          </div>
          <div className={styles.sectionContent}>
            {Object.entries(value).map(([k, v]) => renderValue(k, v, depth + 1))}
          </div>
        </div>
      );
    }

    // Other types
    if (value !== null && value !== undefined) {
      return (
        <div key={key} className={styles.field}>
          <span className={styles.fieldLabel}>{label}:</span>
          <span className={styles.fieldValue}>{String(value)}</span>
        </div>
      );
    }

    return null;
  };

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{title}</h2>
            {version && <span className={styles.version}>{version}</span>}
          </div>
          <div className={styles.headerRight}>
            {data.app_name && <span className={styles.metaTag}>{data.app_name}</span>}
            <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
              <CloseIcon width="14" height="14" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {Object.entries(data).map(([key, value]) => renderValue(key, value, 0))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BlueprintModal;
